import React, { useEffect, useState } from 'react';
import Service from '../MyOrder/Service';
import TassoService from './TassoService';
import './TassoOrders.css';
import { jwtDecode } from 'jwt-decode';
import StatusTimeline from '../../../Components/StatusTimeline/StatusTimeline';
import { useLocation } from 'react-router-dom';

const TassoOrders = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tasso specific state
  const [tassoStatus, setTassoStatus] = useState(null);
  const [tassoLoading, setTassoLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [tassoResult, setTassoResult] = useState(null);
  const [adminStatus, setAdminStatus] = useState('');

  const location = useLocation();

  const passedUserId = location.state?.userId || null;
  const passedOrderId = location.state?.orderId || null;

  let userId = passedUserId;
  if (!userId) {
    const token = sessionStorage.getItem('auth_token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        userId = decoded.user_id;
      } catch (err) {
        console.error('Invalid token:', err);
      }
    }
  }

  useEffect(() => {
    const fetchOrders = async () => {
      if (!userId) {
        setError('User not logged in.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Reusing existing Service to get list of orders
        const response = await Service.getOrderList(userId);

        const formattedOrders = response.data.map((order) => ({
          id: order.id,
          order_number: order.order_number,
          created_at: order.created_at,
          financial_status: order.financial_status,
          fulfillment_status: order.fulfillment_status || 'Pending',
          status_detail: order?.db_order?.status_detail || { code: 'pending', label: 'Pending' },
          total_price: order.total_price,
          currency: order.currency,
          email: order.email,
          billing_address: order.billing_address,
          shipping_address: order.shipping_address,
          products: order.line_items.map((item) => ({
            id: item.id,
            product_name: item.name,
            price: item.price,
            quantity: item.quantity,
            total_discount: item.total_discount,
          })),
          shipping_lines: order.shipping_lines.map((line) => ({
            title: line.title,
            price: line.price || line.discounted_price,
          })),
          total_tax: order.total_tax,
          total_discounts: order.total_discounts,
          tags: order.tags,
          payment_gateway_names: order.payment_gateway_names.join(', '),
          order_status: order?.db_order?.status_detail,
          db_order: order.db_order,
          tasso_order_id: order?.db_order?.tasso_order_id, // Assuming backend provides this
        }));

        setOrders(formattedOrders);
      } catch (err) {
        setError('Failed to fetch orders.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [userId]);

  // Auto select order
  useEffect(() => {
    if (!loading && orders.length > 0 && passedOrderId) {
      const autoSelect = orders.find((o) => o.id === passedOrderId);
      if (autoSelect) {
        setSelectedOrder(autoSelect);
      }
    }
  }, [loading, orders, passedOrderId]);

  // Tasso status mapping - matches backend lowercase format
  const STATUS_MAP = {
    accepted: 'Order Accepted',
    pendingfulfillment: 'At Tasso',
    inlogistics: 'In logistics',
    readytoship: 'Ready to Ship',
    atpatient: 'At Patient',
    intransittolab: 'In Transit To Lab',
    atlab: 'At Lab',
    resultsready: 'Results Ready',
    delayed: 'Delayed',
    problem: 'Problem',
    cancelled: 'Cancelled',
    returned: 'Returned',
    rejected: 'Rejected',
    failed: 'Failed',
    replaced: 'Replaced',
  };

  const getStatusLabel = (tassoData) => {
    if (!tassoData) return 'Status not available';
    const status = tassoData.status;
    if (typeof status === 'string') {
      return STATUS_MAP[status] || status;
    }
    return status?.label || status?.code || 'Unknown';
  };

  const getStatusCode = (tassoData) => {
    if (!tassoData) return '';
    const status = tassoData.status;
    if (typeof status === 'string') {
      return status;
    }
    return status?.code || '';
  };

  // Fetch Tasso Status when an order is selected
  useEffect(() => {
    if (selectedOrder) {
      // Reset Tasso state
      setTassoStatus(null);
      setTassoResult(null);
      setAdminStatus('');

      const fetchTassoStatus = async () => {
        const tassoId = selectedOrder.tasso_order_id;
        const shopifyId = selectedOrder.id;

        // For Mailman, we might not have a tassoId
        if (!tassoId && selectedOrder.db_order?.type === 'mailman') {
          setAdminStatus(selectedOrder.db_order.status_detail?.code || selectedOrder.db_order.status_detail?.label || '');
          return;
        }

        try {
          setTassoLoading(true);
          const res = await TassoService.getTassoOrderStatus(tassoId, shopifyId);
          // Handle both 'results' and 'order' keys in response
          const tassoData = res.data.results || res.data.order;
          if (tassoData) {
            setTassoStatus(tassoData);
            setAdminStatus(getStatusCode(tassoData));
          }
        } catch (e) {
          console.error('Failed to fetch Tasso status', e);
        } finally {
          setTassoLoading(false);
        }
      };

      fetchTassoStatus();
    }
  }, [selectedOrder]);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.name || ''}\n${address.address1 || ''}${
      address.address2 ? `, ${address.address2}` : ''
    }\n${address.city || ''}, ${address.province_code || ''} ${address.zip || ''}\n${address.country || ''}`;
  };

  const handleUpdateStatus = async () => {
    const orderId = tassoStatus?.orderId || tassoStatus?.tasso_order_id || selectedOrder?.id;
    if (!orderId || !adminStatus) return;
    try {
      await TassoService.updateTassoStatus(orderId, adminStatus);
      // Refresh status
      const res = await TassoService.getTassoOrderStatus(orderId, null);
      const tassoData = res.data.results || res.data.order;
      if (tassoData) {
        setTassoStatus(tassoData);
      }
      alert('Status updated successfully');
    } catch (e) {
      alert('Failed to update status');
    }
  };

  const handleFetchResults = async () => {
    const projectId = '94710dc9-19c3-45fe-b9d8-ec566751e9cb'; // Placeholder
    try {
      const res = await TassoService.getTassoResults(projectId);
      if (res.data && res.data.reports) {
        setTassoResult(res.data.reports);
        if (res.data.reports.length > 0 && res.data.reports[0].document_url) {
          window.open(res.data.reports[0].document_url, '_blank');
        }
      }
    } catch (e) {
      console.error('Failed to fetch results', e);
      alert('Failed to fetch results');
    }
  };

  if (loading) return <div className="loader">Loading orders...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="order-container">
      {selectedOrder ? (
        <div key={selectedOrder.id} className="order-card full-view">
          <div className="order-actions">
            <button className="btn back-button" onClick={() => setSelectedOrder(null)}>
              &larr; Back to Orders
            </button>
          </div>

          <div className="order-header">
            <h2>Order #{selectedOrder.order_number}</h2>
            <span className={`status ${selectedOrder.financial_status?.toLowerCase()}`}>{selectedOrder.financial_status}</span>
          </div>

          {/* Tasso Tracking Section */}
          <div className="tasso-section">
            <div className="tasso-header">
              <h3>Tasso Order Tracking</h3>
              {tassoLoading ? (
                <span>Loading status...</span>
              ) : tassoStatus ? (
                <span className="tasso-status">{getStatusLabel(tassoStatus)}</span>
              ) : (
                <span>Status not available</span>
              )}
            </div>

            {(tassoStatus || selectedOrder?.db_order?.type === 'mailman') && (
              <div className="tasso-details">
                {tassoStatus && (
                  <p>
                    <strong>Tasso Order ID:</strong> {tassoStatus.orderId || tassoStatus.tasso_order_id}
                  </p>
                )}

                {/* Results Action */}
                {getStatusCode(tassoStatus) === 'resultsready' && (
                  <div className="tasso-controls">
                    <button className="btn btn-primary" onClick={handleFetchResults}>
                      View Clinical Results
                    </button>
                  </div>
                )}

                {/* Admin/Demo Controls */}
                <div className="admin-controls">
                  <h4>Demo: Update Status</h4>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {selectedOrder?.db_order?.type === 'tasso' ? (
                      <select value={adminStatus} onChange={(e) => setAdminStatus(e.target.value)}>
                        <option value="pendingfulfillment">At Tasso</option>
                        <option value="inlogistics">In logistics</option>
                        <option value="atpatient">At Patient</option>
                        <option value="intransittolab">In Transit To Lab</option>
                        <option value="atlab">At Lab</option>
                        <option value="resultsready">Results Ready</option>
                        <option value="replaced">Replaced</option>
                      </select>
                    ) : (
                      <select value={adminStatus} onChange={(e) => setAdminStatus(e.target.value)}>
                        <option value="Waiting for clinician to assign">Waiting for clinician to assign</option>
                        <option value="Order Placed">Order Placed</option>
                        <option value="Order Processing">Order Processing</option>
                        <option value="In-Transit to Patient">In-Transit to Patient</option>
                        <option value="In-Transit to MES">In-Transit to MES</option>
                        <option value="Test in Process">Test in Process</option>
                        <option value="Test Complete">Test Complete</option>
                      </select>
                    )}
                    <button className="btn btn-outline-primary" onClick={handleUpdateStatus}>
                      Force Update
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <StatusTimeline currentStatus={selectedOrder?.db_order?.status_detail} type={selectedOrder?.db_order?.type} />

          <div className="order-info">
            <p>
              <strong>Order Date:</strong> {formatDate(selectedOrder.created_at)}
            </p>
            <p>
              <strong>Fulfillment:</strong> {selectedOrder.fulfillment_status}
            </p>
            <p>
              <strong>Email:</strong> <span className="black-text">{selectedOrder.email}</span>
            </p>
          </div>

          <div className="addresses">
            <div>
              <h4>Billing Address</h4>
              <pre>{formatAddress(selectedOrder.billing_address)}</pre>
            </div>
            <div>
              <h4>Shipping Address</h4>
              <pre>{formatAddress(selectedOrder.shipping_address)}</pre>
            </div>
          </div>

          <div className="products">
            <h3>Products</h3>
            {selectedOrder.products.map((product) => (
              <div key={product.id} className="product-item">
                <div className="product-name">{product.product_name}</div>
                <div className="product-qty">Qty: {product.quantity}</div>
                <div className="product-price">
                  {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {product.price}
                </div>
              </div>
            ))}
          </div>

          <div className="totals">
            <h3>Totals</h3>
            <div className="order-total">
              <strong>
                Total: {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {selectedOrder.total_price}
              </strong>
            </div>
          </div>
        </div>
      ) : (
        <>
          <h2 className="page-title">Tasso Orders (Demo)</h2>
          {orders.length === 0 ? (
            <div className="no-orders">No orders found.</div>
          ) : (
            <table className="order-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Order Date</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>{order.order_number}</td>
                    <td>{formatDate(order.created_at)}</td>
                    <td>
                      {order.currency === 'USD' ? '$' : order.currency} {order.total_price}
                    </td>
                    <td>{order?.order_status?.label || 'Pending'}</td>
                    <td>
                      <button className="btn btn-primary view-details-btn" onClick={() => setSelectedOrder(order)}>
                        Track & View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default TassoOrders;
